import { apiClientRoutes } from '@/api';
import type {
	DepotProfileSiteAssignmentPayload,
	DepotProfileSiteAssignmentSuccessResponse,
	DepotProfileSiteRemovalOutcome,
} from '@/api/routes/depot/types/companies-sites.types';
import { isDepotProfileSiteAssignmentSuccessResponse } from '@/api/routes/depot/types/companies-sites.types';
import type { ProfileCompanyLookup } from '@/lib/profile-site-removal';
import { formatRelativeDate } from '@/lib/utils';
import { defineStore } from 'pinia';

/** Outcome of a profile write whose toast shows the backend's own message. */
export interface DepotProfileRequestOutcome {
	ok: boolean;
	status?: number;
	message?: string;
}

/**
 * `api()` resolves a 2xx as the axios response and returns the axios error for
 * anything else, so the status and message sit under `.response` on failure.
 */
const toRequestOutcome = (response: any): DepotProfileRequestOutcome => {
	const status = response?.status ?? response?.response?.status;
	const message = response?.data?.message ?? response?.response?.data?.message;

	return {
		ok: Boolean(status && status >= 200 && status <= 299),
		status,
		message,
	};
};

interface DepotProfileStoreState {
	profileUuid: string;
	profileDetails: any;
	profilePlaceDetails: GooglePlaceDetails | null;
	drawerState: boolean;
	profileReviewsSearchDetails: {
		searchQuery: string;
		fromDate?: string;
		toDate?: string;
		reviewRatings: number[];
		orderBy?: 'review_date' | 'review_rating';
		orderDirection?: 'asc' | 'desc';
		total: number;
		limit: number;
		totalPages: number;
		currentPage: number;
		nextPageToken: string;
		pageTokens: string[];
	};
	profileReviews: any[];
}

interface GoogleAddressComponent {
	long_name: string;
	short_name: string;
	types: string[];
}

interface GoogleLatLng {
	lat: number;
	lng: number;
}

interface GoogleViewport {
	northeast: GoogleLatLng;
	southwest: GoogleLatLng;
}

interface GoogleGeometry {
	location: GoogleLatLng;
	viewport: GoogleViewport;
}

interface GooglePlaceDetails {
	address_components: GoogleAddressComponent[];
	adr_address: string;
	business_status: string;
	formatted_address: string;
	geometry: GoogleGeometry;
	icon: string;
	icon_background_color: string;
	icon_mask_base_uri: string;
	name: string;
	place_id: string;
	types: string[];
	url: string;
	utc_offset: number;
	vicinity: string;
}

export const useDepotProfileStore = defineStore('DepotProfileStore', {
	persist: true,
	state: (): DepotProfileStoreState => {
		return {
			profileUuid: '',
			profileDetails: null,
			profilePlaceDetails: {
				address_components: [],
				adr_address: '',
				business_status: '',
				formatted_address: '',
				geometry: {
					location: { lat: 0, lng: 0 },
					viewport: {
						northeast: { lat: 0, lng: 0 },
						southwest: { lat: 0, lng: 0 },
					},
				},
				icon: '',
				icon_background_color: '',
				icon_mask_base_uri: '',
				name: '',
				place_id: '',
				types: [],
				url: '',
				utc_offset: 0,
				vicinity: '',
			},
			drawerState: false,
			profileReviewsSearchDetails: {
				searchQuery: '',
				toDate: undefined,
				fromDate: undefined,
				reviewRatings: [],
				orderBy: undefined,
				orderDirection: 'desc',
				total: 0,
				limit: 10,
				totalPages: 0,
				currentPage: 1,
				nextPageToken: '',
				pageTokens: [''],
			},
			profileReviews: [],
		};
	},
	actions: {
		getTargetProfileUuid(profileUuid?: string) {
			return (
				profileUuid ||
				this.profileUuid ||
				this.profileDetails?.profile_id ||
				this.profileDetails?.id ||
				''
			);
		},
		async getProfileDetails(profileId: string) {
			// Logic to get threads
			console.log('Getting threads');

			this.profileUuid = profileId;

			const profileDetails =
				await apiClientRoutes.depotRoutes.adminProfile.getProfileById(profileId);
			if (!profileDetails || !profileDetails.data) {
				return false;
			}

			this.profileDetails = profileDetails.data;

			return true;
		},
		async requestPastYearReviews(profileUuid?: string): Promise<DepotProfileRequestOutcome> {
			const targetProfileUuid = this.getTargetProfileUuid(profileUuid);

			if (!targetProfileUuid) {
				return { ok: false };
			}

			const payload: any = {
				profile_uuid: targetProfileUuid,
			};

			const response =
				await apiClientRoutes.depotRoutes.reviewsProfiles.postFetchYearReviews(payload);

			return toRequestOutcome(response);
		},
		/**
		 * Queues a weekly Datashake schedule for the profile. The backend only
		 * enqueues, and skips a profile that already has one, so `ok` means queued.
		 */
		async setupReviewSchedule(profileUuid: string): Promise<DepotProfileRequestOutcome> {
			const response =
				await apiClientRoutes.depotRoutes.reviewsDatashake.postSetupDatashakeScheduleForProfile(
					profileUuid,
				);

			return toRequestOutcome(response);
		},
		async requestReviewsFetch(payload: DepotReviewsRequestFetchRequest) {
			const requestPayload: DepotReviewsRequestFetchRequest = {
				...payload,
			};

			const hasExplicitProfileTarget = Boolean(
				requestPayload.profile_uuid ||
				requestPayload.profile_reference ||
				requestPayload.profile_uuids?.length,
			);

			if (!hasExplicitProfileTarget) {
				const targetProfileUuid = this.getTargetProfileUuid();

				if (!targetProfileUuid) {
					return false;
				}

				requestPayload.profile_uuid = targetProfileUuid;
			}

			const response =
				await apiClientRoutes.depotRoutes.reviewsReviews.postRequestFetch(requestPayload);

			if (!response || !response.data) {
				return false;
			}

			return response.data;
		},
		async requestPastMonthReviews(profileUuid?: string) {
			return this.requestReviewsFetch({
				profile_uuid: profileUuid,
				from_date: formatRelativeDate({ months: 1 }),
			});
		},
		async requestPastWeekReviews(profileUuid?: string) {
			return this.requestReviewsFetch({
				profile_uuid: profileUuid,
				from_date: formatRelativeDate({ days: 7 }),
			});
		},
		async openProfilePlaceDetailsDrawer(placeId: string) {
			const placeDetails =
				await apiClientRoutes.depotRoutes.companiesSites.getGooglePlaceDetails(placeId);

			if (!placeDetails || !placeDetails.data) {
				return false;
			}

			this.profilePlaceDetails = placeDetails.data;
			this.drawerState = true;
		},
		async fetchReviewsPage(targetPage: number) {
			if (!this.profileUuid) {
				console.error('Profile UUID is not set.');
				return false;
			}

			if (targetPage < 1) {
				return false;
			}

			const tokenForPage = this.profileReviewsSearchDetails.pageTokens[targetPage - 1] ?? '';

			// If we've never visited this page before (and it's not page 1), we don't have a token to request it.
			if (targetPage > 1 && !tokenForPage) {
				return false;
			}

			const payload: any = {
				page_size: this.profileReviewsSearchDetails.limit,
				profile_uuid: this.profileUuid,
			};

			if (tokenForPage) {
				payload.continuation_token = tokenForPage;
			}

			if (this.profileReviewsSearchDetails.searchQuery) {
				payload.search_term = this.profileReviewsSearchDetails.searchQuery;
			}

			if (this.profileReviewsSearchDetails.fromDate) {
				payload.from_date = this.profileReviewsSearchDetails.fromDate;
			}

			if (this.profileReviewsSearchDetails.toDate) {
				payload.to_date = this.profileReviewsSearchDetails.toDate;
			}

			if (this.profileReviewsSearchDetails.reviewRatings.length) {
				payload.review_ratings = this.profileReviewsSearchDetails.reviewRatings;
			}

			if (this.profileReviewsSearchDetails.orderBy) {
				payload.order_by = this.profileReviewsSearchDetails.orderBy;
				payload.order_direction = this.profileReviewsSearchDetails.orderDirection ?? 'desc';
			}

			const reviews =
				await apiClientRoutes.depotRoutes.reviewsReviews.postGetReviews(payload);

			if (!reviews || !reviews.data) {
				this.profileReviews = [];
				this.profileReviewsSearchDetails.total = 0;
				this.profileReviewsSearchDetails.totalPages = 0;
				this.profileReviewsSearchDetails.nextPageToken = '';
				return false;
			}

			const nextToken: string = reviews.data.continuation_token ?? '';

			this.profileReviews = reviews.data.results;
			this.profileReviewsSearchDetails.total = reviews.data.total_results;

			this.profileReviewsSearchDetails.totalPages = Math.ceil(
				reviews.data.total_results / this.profileReviewsSearchDetails.limit,
			);

			this.profileReviewsSearchDetails.currentPage = targetPage;
			this.profileReviewsSearchDetails.nextPageToken = nextToken;

			// Save the token needed to request the next page.
			// token for page (targetPage + 1) is stored at index targetPage.
			const nextIndex = targetPage;

			if (this.profileReviewsSearchDetails.pageTokens.length <= nextIndex) {
				this.profileReviewsSearchDetails.pageTokens.length = nextIndex + 1;
			}

			this.profileReviewsSearchDetails.pageTokens[nextIndex] = nextToken;

			return true;
		},

		// Starts (or restarts) a search from page 1.
		async searchReviews(profileUuid?: string) {
			if (profileUuid) {
				this.profileUuid = profileUuid;
			}

			if (!this.profileUuid) {
				console.error('Profile UUID is not set.');
				return false;
			}

			this.profileReviewsSearchDetails.currentPage = 1;
			this.profileReviewsSearchDetails.pageTokens = [''];
			this.profileReviewsSearchDetails.nextPageToken = '';

			return this.fetchReviewsPage(1);
		},
		async searchReviewsNextPage() {
			const currentPage = this.profileReviewsSearchDetails.currentPage || 1;
			const tokenForNextPage = this.profileReviewsSearchDetails.pageTokens[currentPage] ?? '';

			if (!tokenForNextPage) {
				return false;
			}

			return this.fetchReviewsPage(currentPage + 1);
		},

		async searchReviewsPrevPage() {
			const currentPage = this.profileReviewsSearchDetails.currentPage || 1;

			if (currentPage <= 1) {
				return false;
			}

			return this.fetchReviewsPage(currentPage - 1);
		},
		resetSearchDetails() {
			this.profileReviewsSearchDetails = {
				searchQuery: '',
				toDate: undefined,
				fromDate: undefined,
				reviewRatings: [],
				orderBy: undefined,
				orderDirection: 'desc',
				total: 0,
				totalPages: 0,
				currentPage: 1,
				limit: 10,
				nextPageToken: '',
				pageTokens: [''],
			};
		},
		async getProfileCompanyData(profileUuid: string) {
			const response =
				await apiClientRoutes.depotRoutes.companiesCompany.getCompanyByProfileUuid(
					profileUuid,
				);

			if (!response || !response.data) {
				return null;
			}

			return response.data;
		},
		/**
		 * The same lookup as `getProfileCompanyData`, reporting whether it resolved.
		 *
		 * `getProfileCompanyData` collapses a failed request and an empty result to
		 * null, which is fine for the parentage displays that read it: both render as
		 * "not assigned". It is not fine here. Telling an operator their profile is
		 * attached to no site, when the truth is that the companies API is down,
		 * invites them to close the dialog on a removal that was still needed.
		 *
		 * A 404 counts as resolved-with-nothing rather than as a failure, following
		 * the depot companies convention of answering "nothing matched" with a 404.
		 */
		async lookupProfileCompany(profileUuid: string): Promise<ProfileCompanyLookup> {
			const response =
				await apiClientRoutes.depotRoutes.companiesCompany.getCompanyByProfileUuid(
					profileUuid,
				);

			// `api()` hands back the AxiosError rather than throwing on a non-2xx, so a
			// failure's status lives under `response`.
			const status = response?.status ?? response?.response?.status;

			if (status === 404) {
				return { ok: true, company: null };
			}

			if (!status || status < 200 || status > 299) {
				return { ok: false };
			}

			return { ok: true, company: response?.data ?? null };
		},
		/**
		 * Detach a profile from one site.
		 *
		 * Returns the endpoint's outcome rather than a boolean, as
		 * `reassignSiteToCompany` does: the four responses mean four different things
		 * and only the 409 and the 500 are worth retrying. See
		 * `removalOutcome` in `src/lib/profile-site-removal.ts` for that rule, and the
		 * route's own comment for why a 404 is terminal.
		 *
		 * The 200 body comes back alongside, so the success copy can name the site and
		 * company the endpoint resolved rather than echoing what was sent.
		 */
		async removeProfileFromSite(
			profileUuid: string,
			siteUuid: string,
		): Promise<DepotProfileSiteRemovalOutcome> {
			const removal = await apiClientRoutes.depotRoutes.companiesSites.postRemoveProfileFromSite(
				{
					profile_uuid: profileUuid,
					site_uuid: siteUuid,
				},
			);

			const status = removal?.status ?? removal?.response?.status;
			const message = removal?.data?.message ?? removal?.response?.data?.message;
			const ok = Boolean(status && status >= 200 && status <= 299);

			return {
				ok,
				status,
				message,
				...(ok && removal?.data ? { data: removal.data } : {}),
			};
		},
		async assignProfileToSite(
			payload: DepotProfileSiteAssignmentPayload,
		): Promise<DepotProfileSiteAssignmentSuccessResponse | false> {
			const response =
				await apiClientRoutes.depotRoutes.companiesSites.postAssignProfileToSite(payload);

			if (!isDepotProfileSiteAssignmentSuccessResponse(response?.data)) {
				return false;
			}

			return response.data;
		},
		async subscribeClientToProfile(
			profileUuid: string,
			payload: { client_id?: string; subscribed: boolean; fetch_responses: boolean },
		): Promise<{ message: string } | null> {
			const response = await apiClientRoutes.depotRoutes.adminProfile.postSubscribeAndFetch(
				profileUuid,
				payload,
			);

			if (!response || !response.data) {
				return null;
			}

			return response.data;
		},
		// async deleteProfile(profileUuid: any) {
		//     const deletedProfile = await apiClientRoutes.depotRoutes.adminProfile.(profileUuid);
		//     return deletedProfile;
		// }
	},
});
